/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import { API_URLS } from '@/constants/apiUrls';
import { getAxios } from '@/lib/axios';
import { type AxiosInstance } from 'axios';
import { type CertonaPricing } from '@/types';
import type {
  AffinityRelatedProductsOptionsACES,
  AffinityRelatedProductsResponseGCP,
  AffinityRelatedProductsData,
  AffinityRelatedProductACES,
} from '../interface';
import environmentConfig from '@/config/environment';

const GCP_URL = `${requestBaseURLGCPExtOrInt}/sls/b2c/product-discovery-product-data/v1/related-parts`;
const GCP_ES_URL = `${requestBaseURLGCPExtOrInt}/sls/b2c/product-discovery-product-data/v2/related-parts`;

export const getAffinityRelatedProductsResultsGCP = async (
  options: AffinityRelatedProductsOptionsACES,
  axiosInstance?: AxiosInstance,
): Promise<AffinityRelatedProductsData> => {
  const { inventorySource, useElasticSearch = false, ...params } = options;
  const headers: Record<string, string> = {};
  if (inventorySource) {
    headers['inventory-source'] = String(inventorySource);
  }
  const url = useElasticSearch ? GCP_ES_URL : GCP_URL;
  const {
    data: { relatedProducts },
  } = await getAxios(axiosInstance).get<AffinityRelatedProductsResponseGCP>(url, {
    withCredentials: !environmentConfig.DISABLE_GCP_API_CREDENTIALS,
    params,
    headers,
  });

  const affinityData = relatedProducts.slice(0, options?.recordsPerPage || relatedProducts.length);

  if (!affinityData.length) {
    return null;
  }

  const skuNumbers = affinityData.map((part) => part.skuId).join(',');
  const { data: certonaPricingForAffinityItems } = await getAxios(axiosInstance).get<
    CertonaPricing[]
  >(`${API_URLS.CERTONA_PRICING}${skuNumbers}?storeNumber=${options.storeId ?? ''}`);

  return selectorGCP(affinityData, certonaPricingForAffinityItems);
};

const selectorGCP = (
  affinityData: AffinityRelatedProductACES[],
  certonaPricing: CertonaPricing[],
): AffinityRelatedProductsData => {
  const relatedProductsWithPricing = affinityData.map((product) => {
    const pricingForProduct = certonaPricing.find((part) => part.skuId === product.skuId);
    const {
      productDetailsPageUrl: seoUrl,
      partGroupId: productId,
      ...affinityRelatedProduct
    } = product;

    if (!pricingForProduct) {
      // eslint-disable-next-line no-console
      console.error(`Missing Certona pricing for GCP related parts SKU: ${product.skuId}`);
      return null;
    }

    return {
      ...affinityRelatedProduct,
      ...pricingForProduct,
      productId,
      seoUrl,
    };
  });

  return relatedProductsWithPricing.filter(Boolean) as AffinityRelatedProductsData;
};
